import { getCurrentLocalTimestamp, localNowDateString, toLocalDateString } from '@/app/utils/localDate';
import { syncJournalEntryDateByDocumentRefs } from '@/app/services/journalTransactionDateSyncService';
/**
 * Purchase Accounting Service (Phase 5: one contract)
 *
 * Source lock: journal_entries + journal_entry_lines + accounts only.
 * COA: 1200 Inventory, 2000 AP, 5210 Discount Received, freight/labor/extra → Dr Inventory Cr AP.
 * Payment isolation: document JEs never touch payment_id; payment has its own flow.
 *
 * Purchase create: in PurchaseContext – Dr Inventory (1200), Cr AP (2000); discount → Dr AP Cr Discount Received (5210); other charges → Dr Inventory Cr AP.
 * Purchase edit: delta JEs only (subtotal, discount, otherCharges); no blanket reversal; payment untouched unless payment changed.
 */

import { supabase } from '@/lib/supabase';
import { pickCanonicalInventoryAssetAccount } from '@/app/lib/inventoryAccountRouting';
import { canPostAccountingForPurchaseStatus } from '@/app/lib/postingStatusGate';
import { accountingService, type JournalEntry, type JournalEntryLine } from './accountingService';
import { resolvePayablePostingAccountId } from './partySubledgerAccountService';
import { documentNumberService } from './documentNumberService';
import {
  getFreightSettlement,
  isCourierFreightChargeType,
} from '@/app/wholesale/wholesaleImportPurchaseCalc';
import { resolveCourierPayableForPurchase } from '@/app/wholesale/wholesaleImportPurchase';

export type PurchaseAccountingSnapshot = {
  total: number;
  subtotal: number;
  discount: number;
  /** Sum of charges that are NOT discount (freight, labor, extra, etc.) */
  otherCharges: number;
};

/** Sum amounts from charges by type. */
function sumCharges(
  charges: { charge_type?: string; chargeType?: string; amount?: number }[],
  predicate: (t: string) => boolean
): number {
  return (charges || []).reduce((sum, c) => {
    const t = ((c.charge_type ?? c.chargeType) ?? '').toLowerCase();
    const amt = Number(c.amount ?? 0) || 0;
    return predicate(t) ? sum + amt : sum;
  }, 0);
}

/** Human-readable label for purchase extra-charge `charge_type` (journal line + statements). */
function purchaseLedgerChargeLabel(typeRaw: string): string {
  const t = String(typeRaw || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const labels: Record<string, string> = {
    freight: 'Freight / shipping',
    shipping: 'Freight / shipping',
    cargo: 'Cargo',
    courier: 'Courier',
    loading: 'Loading',
    unloading: 'Unloading',
    labor: 'Labor',
    labour: 'Labor',
    stitching: 'Stitching',
    stitch: 'Stitching',
    handling: 'Handling',
    insurance: 'Insurance',
    demurrage: 'Demurrage',
    customs: 'Customs / duty',
    duty: 'Customs / duty',
    discount: 'Discount',
    other: 'Other charge',
  };
  if (!t) return 'Extra charge';
  return labels[t] || t.replace(/_/g, ' ');
}

export type PurchaseDocumentJournalLineSpec = {
  account_id: string;
  debit: number;
  credit: number;
  description: string;
};

/** Build canonical purchase document JE lines (retail supplier freight vs wholesale courier clearance). */
export async function buildPurchaseDocumentJournalLines(params: {
  companyId: string;
  purchaseId: string;
  poNo: string;
  supplierName: string;
  subtotal: number;
  discount: number;
  tax: number;
  shippingCost: number;
  freightSettlement: string;
  clearanceCourierId?: string | null;
  supplierContactId?: string | null;
  charges?: Array<{ charge_type?: string; chargeType?: string; amount?: number }>;
  inventoryAccountId: string;
  apAccountId: string;
  discountAccountId?: string | null;
}): Promise<PurchaseDocumentJournalLineSpec[]> {
  const {
    companyId,
    poNo,
    supplierName,
    subtotal,
    discount,
    tax,
    shippingCost,
    freightSettlement,
    clearanceCourierId,
    supplierContactId,
    charges = [],
    inventoryAccountId,
    apAccountId,
    discountAccountId,
  } = params;

  const courierMode = getFreightSettlement({ freight_settlement: freightSettlement }) === 'courier' && !!clearanceCourierId;
  let courierPayableId: string | null = null;
  if (courierMode && clearanceCourierId) {
    courierPayableId = await resolveCourierPayableForPurchase(companyId, clearanceCourierId);
  }

  const lines: PurchaseDocumentJournalLineSpec[] = [];
  const itemsSubtotal = Number(subtotal) || 0;

  if (itemsSubtotal > 0) {
    lines.push(
      { account_id: inventoryAccountId, debit: itemsSubtotal, credit: 0, description: `Inventory purchase ${poNo}` },
      { account_id: apAccountId, debit: 0, credit: itemsSubtotal, description: `Payable to ${supplierName}` },
    );
  }

  const taxAmt = Number(tax) || 0;
  if (taxAmt > 0) {
    lines.push(
      { account_id: inventoryAccountId, debit: taxAmt, credit: 0, description: `Tax (purchase) ${poNo}` },
      { account_id: apAccountId, debit: 0, credit: taxAmt, description: `Payable — tax ${supplierName}` },
    );
  }

  for (const c of charges) {
    const amount = Number(c?.amount ?? 0);
    const type = String(c?.charge_type ?? c?.chargeType ?? '').toLowerCase();
    const chargeLabel = purchaseLedgerChargeLabel(type);
    if (amount <= 0) continue;
    if (type === 'discount' && discountAccountId) {
      lines.push(
        { account_id: apAccountId, debit: amount, credit: 0, description: 'Purchase discount (purchase)' },
        { account_id: discountAccountId, debit: 0, credit: amount, description: 'Discount received (purchase)' },
      );
      continue;
    }
    if (courierMode && isCourierFreightChargeType(type)) {
      continue;
    }
    const creditAccountId = apAccountId;
    lines.push(
      { account_id: inventoryAccountId, debit: amount, credit: 0, description: `${chargeLabel} (purchase)` },
      { account_id: creditAccountId, debit: 0, credit: amount, description: `Payable - ${chargeLabel}` },
    );
  }

  if (Number(discount) > 0 && discountAccountId && !charges.some((c) => String(c?.charge_type ?? c?.chargeType ?? '').toLowerCase() === 'discount')) {
    lines.push(
      { account_id: apAccountId, debit: discount, credit: 0, description: 'Purchase discount (purchase)' },
      { account_id: discountAccountId, debit: 0, credit: discount, description: 'Discount received (purchase)' },
    );
  }

  const clearance = Number(shippingCost) || 0;
  if (clearance > 0) {
    if (courierMode && courierPayableId) {
      lines.push(
        { account_id: inventoryAccountId, debit: clearance, credit: 0, description: `Clearance / freight (purchase) ${poNo}` },
        { account_id: courierPayableId, debit: 0, credit: clearance, description: `Courier clearance — ${supplierName}` },
      );
    } else {
      lines.push(
        { account_id: inventoryAccountId, debit: clearance, credit: 0, description: `Freight (purchase)` },
        { account_id: apAccountId, debit: 0, credit: clearance, description: `Payable — freight` },
      );
    }
  }

  void supplierContactId;
  return lines;
}

/** Build accounting snapshot from purchase row + charges for delta comparison. */
export function getPurchaseAccountingSnapshot(purchase: {
  total?: number;
  subtotal?: number;
  discount_amount?: number;
  discount?: number;
  shipping_cost?: number;
  charges?: { charge_type?: string; chargeType?: string; amount?: number }[];
  purchase_charges?: { charge_type?: string; chargeType?: string; amount?: number }[];
}): PurchaseAccountingSnapshot {
  const total = Number(purchase?.total ?? 0) || 0;
  const charges = Array.isArray(purchase?.charges) ? purchase.charges : (Array.isArray((purchase as any).purchase_charges) ? (purchase as any).purchase_charges : []);
  const discount = Number(purchase?.discount_amount ?? purchase?.discount ?? 0) || sumCharges(charges, t => t === 'discount');
  // Include shipping_cost column (form stores freight here, not in purchase_charges)
  const shippingCost = Number((purchase as any)?.shipping_cost ?? 0) || 0;
  const chargesOther = sumCharges(charges, t => t !== 'discount');
  const otherCharges = chargesOther + shippingCost;
  const subtotal = Number(purchase?.subtotal ?? 0) || total + discount - otherCharges;
  if (subtotal <= 0 && total > 0) {
    return { total, subtotal: total + discount - otherCharges, discount, otherCharges };
  }
  return { total, subtotal, discount, otherCharges };
}

/**
 * Canonical purchase document JE:
 * - reference_type = 'purchase'
 * - reference_id = purchase id
 * - payment_id IS NULL
 * - not void
 */
export function purchaseDocumentJournalFingerprint(companyId: string, purchaseId: string): string {
  return `purchase_document:${companyId}:${purchaseId}`;
}

export async function findActiveCanonicalPurchaseDocumentJournalEntryId(purchaseId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id')
    .eq('reference_type', 'purchase')
    .eq('reference_id', purchaseId)
    .is('payment_id', null)
    .or('is_void.is.null,is_void.eq.false')
    .order('created_at', { ascending: true })
    .limit(1);
  if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) return null;
  if (error) return null;
  const row = (data as { id: string }[] | null)?.[0];
  return row?.id ?? null;
}

export async function listActiveCanonicalPurchaseDocumentJournalEntryIds(purchaseId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, is_void')
    .eq('reference_type', 'purchase')
    .eq('reference_id', purchaseId)
    .is('payment_id', null)
    .or('is_void.is.null,is_void.eq.false');
  if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) return [];
  if (error || !data?.length) return [];
  return (data as { id: string; is_void?: boolean | null }[])
    .filter((r) => r.is_void !== true)
    .map((r) => r.id)
    .filter(Boolean);
}

/** Backward-compatible boolean guard used by context flows. */
async function purchaseJournalEntryExists(purchaseId: string): Promise<boolean> {
  const id = await findActiveCanonicalPurchaseDocumentJournalEntryId(purchaseId);
  return !!id;
}

async function assertPurchaseEligibleForDocumentJournal(purchaseId: string, poNo: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('purchases')
    .select('id, status, po_no')
    .eq('id', purchaseId)
    .maybeSingle();
  if (error || !data) {
    console.warn('[purchaseAccountingService] Cannot load purchase for accounting guard:', purchaseId, error?.message);
    return false;
  }
  const status = (data as { status?: string }).status;
  const dbPo = String((data as { po_no?: string }).po_no ?? '').trim() || poNo;
  if (!canPostAccountingForPurchaseStatus(status)) {
    console.warn(
      `[purchaseAccountingService] Blocked document JE for ${dbPo}: purchase status is "${status}" (only final/received may post inventory/AP document).`
    );
    return false;
  }
  if (!dbPo) {
    console.warn(
      `[purchaseAccountingService] Blocked document JE for purchase ${purchaseId}: posted status but po_no is empty — assign final PO number before posting.`
    );
    return false;
  }
  return true;
}

/** Posting inventory asset: canonical leaf **1200** (never chart group **1090**). */
async function resolveInventoryGlAccountIdForPurchase(companyId: string): Promise<string | null> {
  const { data: invRows, error } = await supabase
    .from('accounts')
    .select('id, code, type, name, is_group, is_active')
    .eq('company_id', companyId)
    .or('code.eq.1200,code.eq.1500,type.eq.inventory');
  if (error) {
    console.warn('[purchaseAccountingService] resolveInventoryGlAccountId:', error.message);
    return null;
  }
  const rows = (invRows || []).map(
    (a: { id: string; code?: string; type?: string; name?: string; is_group?: boolean; is_active?: boolean }) => ({
      id: a.id,
      code: a.code,
      type: a.type,
      name: a.name,
      is_group: a.is_group === true,
      isActive: a.is_active !== false,
    })
  );
  return pickCanonicalInventoryAssetAccount(rows)?.id ?? null;
}

export async function createPurchaseJournalEntry(params: {
  purchaseId: string;
  companyId: string;
  branchId?: string | null;
  total: number;
  subtotal?: number;
  poNo: string;
  supplierName: string;
  entryDate?: string;
  charges?: Array<{ charge_type?: string; chargeType?: string; amount?: number }>;
  createdBy?: string | null;
}): Promise<string | null> {
  const {
    purchaseId,
    companyId,
    branchId,
    total,
    subtotal,
    poNo,
    supplierName,
    entryDate,
    charges = [],
    createdBy,
  } = params;
  if (!purchaseId || !companyId) return null;
  if ((Number(total) || 0) <= 0) return null;

  const eligible = await assertPurchaseEligibleForDocumentJournal(purchaseId, poNo);
  if (!eligible) return null;

  const resolvedEntryDate = entryDate ? toLocalDateString(entryDate) : localNowDateString();

  const existingId = await findActiveCanonicalPurchaseDocumentJournalEntryId(purchaseId);
  if (existingId) {
    console.log(`[purchaseAccountingService] Canonical purchase document JE already exists for ${poNo}, reusing ${existingId}`);
    await syncJournalEntryDateByDocumentRefs({
      companyId,
      referenceTypes: ['purchase', 'purchase_adjustment'],
      referenceId: purchaseId,
      entryDate: resolvedEntryDate,
    });
    return existingId;
  }

  const { data: purRow } = await supabase
    .from('purchases')
    .select('supplier_id, discount_amount, tax_amount, shipping_cost, freight_settlement, clearance_courier_id')
    .eq('id', purchaseId)
    .maybeSingle();
  const supplierContactId = (purRow as { supplier_id?: string | null } | null)?.supplier_id ?? null;
  const headerDiscount = Number((purRow as { discount_amount?: number } | null)?.discount_amount ?? 0) || 0;
  const headerTax = Number((purRow as { tax_amount?: number } | null)?.tax_amount ?? 0) || 0;
  const headerShipping = Number((purRow as { shipping_cost?: number } | null)?.shipping_cost ?? 0) || 0;
  const freightSettlement = String((purRow as { freight_settlement?: string } | null)?.freight_settlement ?? 'supplier');
  const clearanceCourierId = (purRow as { clearance_courier_id?: string | null } | null)?.clearance_courier_id ?? null;

  let inventoryAccountId: string | null = await resolveInventoryGlAccountIdForPurchase(companyId);
  let apAccountId: string | null = null;
  let discountAccountId: string | null = null;
  if (!inventoryAccountId) {
    console.warn('[purchaseAccountingService] No canonical inventory (1200) for company', companyId);
    return null;
  }
  const { data: apRows } = await supabase
    .from('accounts')
    .select('id, code')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .or('name.ilike.%Accounts Payable%,code.eq.2000');
  const apList = (apRows || []) as { id: string; code: string }[];
  apAccountId = apList.find((a) => a.code === '2000')?.id ?? apList[0]?.id ?? null;
  const apPartyCreate = await resolvePayablePostingAccountId(companyId, supplierContactId || undefined);
  if (apPartyCreate) apAccountId = apPartyCreate;
  const { data: discRows } = await supabase
    .from('accounts')
    .select('id, code')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .or('code.eq.5210,name.ilike.%Discount Received%,name.ilike.%Purchase Discount%,name.ilike.%Operating Expense%');
  const discList = (discRows || []) as { id: string; code: string }[];
  discountAccountId = discList.find((a) => a.code === '5210')?.id ?? discList[0]?.id ?? null;
  if (!inventoryAccountId || !apAccountId) return null;

  const itemsSubtotal = Number(subtotal ?? 0) || Number(total) || 0;
  const entryNo = await documentNumberService.getNextJournalEntryNumber(companyId, branchId);
  const normalizedCharges = [...charges];
  const hasDiscountLine = normalizedCharges.some((c) => String(c?.charge_type ?? c?.chargeType ?? '').toLowerCase() === 'discount');
  if (headerDiscount > 0 && !hasDiscountLine) {
    normalizedCharges.push({ charge_type: 'discount', amount: headerDiscount });
  }

  const entry: JournalEntry = {
    id: '',
    company_id: companyId,
    branch_id: branchId && branchId !== 'all' ? branchId : undefined,
    entry_no: entryNo,
    entry_date: resolvedEntryDate,
    description: `Purchase ${poNo} from ${supplierName}`,
    reference_type: 'purchase',
    reference_id: purchaseId,
    created_by: createdBy || undefined,
    action_fingerprint: purchaseDocumentJournalFingerprint(companyId, purchaseId),
  };

  const lineSpecs = await buildPurchaseDocumentJournalLines({
    companyId,
    purchaseId,
    poNo,
    supplierName,
    subtotal: itemsSubtotal,
    discount: headerDiscount,
    tax: headerTax,
    shippingCost: headerShipping,
    freightSettlement,
    clearanceCourierId,
    supplierContactId,
    charges: normalizedCharges,
    inventoryAccountId,
    apAccountId,
    discountAccountId,
  });
  const lines: JournalEntryLine[] = lineSpecs.map((l) => ({
    id: '',
    journal_entry_id: '',
    account_id: l.account_id,
    debit: l.debit,
    credit: l.credit,
    description: l.description,
  }));
  if (!lines.length) return null;
  const result = await accountingService.createEntry(entry, lines);
  return (result as { id?: string } | null)?.id ?? null;
}

async function postPurchaseAdjustmentJE(
  companyId: string,
  branchId: string | undefined,
  purchaseId: string,
  entryDate: string,
  createdBy: string | null,
  description: string,
  lines: { accountId: string; debit: number; credit: number; description: string }[]
): Promise<void> {
  const exists = await accountingService.hasExistingPurchaseAdjustmentByDescription(companyId, purchaseId, description);
  if (exists) {
    if (import.meta.env?.DEV) console.log('[purchaseAccountingService] Skipping duplicate purchase_adjustment JE:', description.slice(0, 60));
    return;
  }
  const entryNo = await documentNumberService.getNextJournalEntryNumber(companyId, branchId);
  const fingerprint = `purchase_adjustment:${companyId}:${purchaseId}:${description}`;
  const entry: JournalEntry = {
    id: '',
    company_id: companyId,
    branch_id: branchId,
    entry_no: entryNo,
    entry_date: entryDate,
    description,
    reference_type: 'purchase_adjustment',
    reference_id: purchaseId,
    created_by: createdBy || undefined,
    action_fingerprint: fingerprint,
  };
  const lineRows: JournalEntryLine[] = lines.map((l) => ({
    id: '',
    journal_entry_id: '',
    account_id: l.accountId,
    debit: l.debit,
    credit: l.credit,
    description: l.description,
  }));
  await accountingService.createEntry(entry, lineRows);
}

/**
 * Post component-level adjustment JEs for a purchase edit. Original purchase JE is NOT deleted.
 * Only posts deltas for: items subtotal, discount, other charges (freight/labor/extra).
 * Payment is NEVER touched (handled by payments table + supplierPaymentService).
 */
export async function postPurchaseEditAdjustments(params: {
  companyId: string;
  branchId: string | null;
  purchaseId: string;
  poNo: string;
  entryDate: string;
  createdBy: string | null;
  oldSnapshot: PurchaseAccountingSnapshot;
  newSnapshot: PurchaseAccountingSnapshot;
  supplierName: string;
}): Promise<{ adjustmentCount: number }> {
  const { companyId, branchId, purchaseId, poNo, entryDate, createdBy, oldSnapshot, newSnapshot, supplierName } = params;
  let adjustmentCount = 0;

  const branchIdSafe = branchId && branchId !== 'all' ? branchId : undefined;

  const { data: purSupplier } = await supabase.from('purchases').select('supplier_id').eq('id', purchaseId).maybeSingle();
  const supplierContactId = (purSupplier as { supplier_id?: string | null } | null)?.supplier_id ?? null;

  // Phase 3: Payment isolation – only document deltas (inventory, AP, discount); never touch payment_id JEs.
  let inventoryAccountId: string | null = await resolveInventoryGlAccountIdForPurchase(companyId);
  let apAccountId: string | null = null;
  let discountAccountId: string | null = null;
  const { data: apRows } = await supabase.from('accounts').select('id, code').eq('company_id', companyId).eq('is_active', true).or('name.ilike.%Accounts Payable%,code.eq.2000');
  const apList = (apRows || []) as { id: string; code: string }[];
  apAccountId = apList.find((a) => a.code === '2000')?.id ?? apList[0]?.id ?? null;
  const apPartyId = await resolvePayablePostingAccountId(companyId, supplierContactId || undefined);
  const effectiveApId = apPartyId || apAccountId;
  const { data: discRows } = await supabase.from('accounts').select('id, code').eq('company_id', companyId).eq('is_active', true).or('code.eq.5210,name.ilike.%Discount Received%,name.ilike.%Purchase Discount%,name.ilike.%Operating Expense%');
  const discList = (discRows || []) as { id: string; code: string }[];
  discountAccountId = discList.find((a) => a.code === '5210')?.id ?? discList[0]?.id ?? null;

  if (!inventoryAccountId || !effectiveApId) {
    console.warn('[purchaseAccountingService] Missing Inventory or AP account, skipping adjustments');
    return { adjustmentCount };
  }

  const fmt = (n: number) => Number(n).toLocaleString();
  const delta = (n: number) => (n > 0 ? `+Rs ${fmt(n)}` : `-Rs ${fmt(-n)}`);

  // 1) Items subtotal delta (purchase value) – Dr Inventory Cr AP (or reverse)
  const deltaSubtotal = Math.round((newSnapshot.subtotal - oldSnapshot.subtotal) * 100) / 100;
  if (deltaSubtotal !== 0) {
    const desc = `Purchase edit ${poNo}: Subtotal Rs ${fmt(oldSnapshot.subtotal)} → Rs ${fmt(newSnapshot.subtotal)} (${delta(deltaSubtotal)}) – ${supplierName}`;
    const abs = Math.abs(deltaSubtotal);
    if (deltaSubtotal > 0) {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: inventoryAccountId, debit: abs, credit: 0, description: `Inventory ${delta(deltaSubtotal)} – ${poNo}` },
        { accountId: effectiveApId!, debit: 0, credit: abs, description: `AP ${supplierName} ${delta(deltaSubtotal)} – ${poNo}` },
      ]);
      adjustmentCount++;
    } else {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: effectiveApId!, debit: abs, credit: 0, description: `AP ${supplierName} ${delta(deltaSubtotal)} – ${poNo}` },
        { accountId: inventoryAccountId, debit: 0, credit: abs, description: `Inventory ${delta(deltaSubtotal)} – ${poNo}` },
      ]);
      adjustmentCount++;
    }
  }

  // 2) Discount delta – Dr AP Cr Discount Received (or reverse)
  const deltaDiscount = Math.round((newSnapshot.discount - oldSnapshot.discount) * 100) / 100;
  if (deltaDiscount !== 0 && discountAccountId) {
    const desc = `Purchase edit ${poNo}: Discount Rs ${fmt(oldSnapshot.discount)} → Rs ${fmt(newSnapshot.discount)} (${delta(deltaDiscount)}) – ${supplierName}`;
    const abs = Math.abs(deltaDiscount);
    if (deltaDiscount > 0) {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: effectiveApId!, debit: abs, credit: 0, description: `Discount +Rs ${fmt(abs)} reduces AP – ${poNo}` },
        { accountId: discountAccountId, debit: 0, credit: abs, description: `Discount received Rs ${fmt(abs)} – ${poNo}` },
      ]);
      adjustmentCount++;
    } else {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: discountAccountId, debit: abs, credit: 0, description: `Discount reversal Rs ${fmt(abs)} – ${poNo}` },
        { accountId: effectiveApId!, debit: 0, credit: abs, description: `Discount reversal increases AP – ${poNo}` },
      ]);
      adjustmentCount++;
    }
  }

  // 3) Other charges (freight/labor/extra) – Dr Inventory Cr AP (or reverse)
  const deltaOther = Math.round((newSnapshot.otherCharges - oldSnapshot.otherCharges) * 100) / 100;
  if (deltaOther !== 0) {
    const desc = `Purchase edit ${poNo}: Freight/Extra Rs ${fmt(oldSnapshot.otherCharges)} → Rs ${fmt(newSnapshot.otherCharges)} (${delta(deltaOther)}) – ${supplierName}`;
    const abs = Math.abs(deltaOther);
    if (deltaOther > 0) {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: inventoryAccountId, debit: abs, credit: 0, description: `Freight/Extra +Rs ${fmt(abs)} – ${poNo}` },
        { accountId: effectiveApId!, debit: 0, credit: abs, description: `AP ${supplierName} freight +Rs ${fmt(abs)} – ${poNo}` },
      ]);
      adjustmentCount++;
    } else {
      await postPurchaseAdjustmentJE(companyId, branchIdSafe, purchaseId, entryDate, createdBy, desc, [
        { accountId: effectiveApId!, debit: abs, credit: 0, description: `AP ${supplierName} freight -Rs ${fmt(abs)} – ${poNo}` },
        { accountId: inventoryAccountId, debit: 0, credit: abs, description: `Freight/Extra -Rs ${fmt(abs)} – ${poNo}` },
      ]);
      adjustmentCount++;
    }
  }

  return { adjustmentCount };
}

/**
 * Cancel of posted purchase: mirror canonical document JE lines into purchase_reversal (original document JE kept for audit).
 * Idempotent: returns existing active reversal JE id if present.
 */
export async function reversePurchaseDocumentJournalEntry(params: {
  purchaseId: string;
  companyId: string;
  branchId?: string | null;
  poNo: string;
  performedBy?: string | null;
}): Promise<string | null> {
  const { purchaseId, companyId, branchId, poNo, performedBy } = params;
  if (!purchaseId || !companyId) return null;

  const { data: revExisting } = await supabase
    .from('journal_entries')
    .select('id')
    .eq('reference_type', 'purchase_reversal')
    .eq('reference_id', purchaseId)
    .is('payment_id', null)
    .or('is_void.is.null,is_void.eq.false')
    .limit(1);
  const revRow = (revExisting as { id: string }[] | null)?.[0];
  if (revRow?.id) return revRow.id;

  const docId = await findActiveCanonicalPurchaseDocumentJournalEntryId(purchaseId);
  if (!docId) {
    console.log(`[purchaseAccountingService] No canonical purchase document JE for ${poNo}, skipping reversal`);
    return null;
  }

  const { data: lines, error: linesErr } = await supabase
    .from('journal_entry_lines')
    .select('account_id, debit, credit, description')
    .eq('journal_entry_id', docId);
  if (linesErr || !lines?.length) {
    console.warn('[purchaseAccountingService] Could not load lines for purchase reversal:', linesErr?.message);
    return null;
  }

  const entryNo = await documentNumberService.getNextJournalEntryNumber(companyId, branchId);
  // Cancellation stamps the day of cancel (not original po_date).
  const entryDate = localNowDateString();
  const entry: JournalEntry = {
    id: '',
    company_id: companyId,
    branch_id: branchId && branchId !== 'all' ? branchId : undefined,
    entry_no: entryNo,
    entry_date: entryDate,
    description: `Purchase cancelled – reversal of ${poNo}`,
    reference_type: 'purchase_reversal',
    reference_id: purchaseId,
    created_by: performedBy || undefined,
  };

  const lineRows: JournalEntryLine[] = (lines as { account_id: string; debit: number; credit: number; description?: string }[]).map(
    (l) => ({
      id: '',
      journal_entry_id: '',
      account_id: l.account_id,
      debit: Number(l.credit) || 0,
      credit: Number(l.debit) || 0,
      description: l.description ? `Reversal: ${l.description}` : `Reversal – ${poNo}`,
    })
  );

  try {
    const result = await accountingService.createEntry(entry, lineRows);
    return (result as { id?: string })?.id ?? null;
  } catch (e: any) {
    console.error('[purchaseAccountingService] Failed to create purchase reversal JE:', e?.message);
    return null;
  }
}

export const purchaseAccountingService = {
  getPurchaseAccountingSnapshot,
  postPurchaseEditAdjustments,
  purchaseJournalEntryExists,
  createPurchaseJournalEntry,
  findActiveCanonicalPurchaseDocumentJournalEntryId,
  listActiveCanonicalPurchaseDocumentJournalEntryIds,
  purchaseDocumentJournalFingerprint,
  reversePurchaseDocumentJournalEntry,
};
